import React, { useState, useEffect } from "react";
import ApiCaller from "../../api/ApiCaller";
import {
    ADD_UPDATE,
    GENERIC_PAGINATED_KEY_VALUE_LIST,
    GENERIC_REQUEST,
    getApiPath,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
    PARTICULAR_RESPONSE_PARAMETER_LIST_GET,
    VIEW,
} from "../../api/ApiConst";
import { DashboardItem } from "../../models/DashboardItem";
import { get } from "lodash";
import "../../css/DashboardItemDetail.css"; // Ensure this file exists
import { toast } from "react-toastify";

const mapRowToFormState = (attributes, row) => {
    const initialState = {};
    attributes.forEach((attribute) => {
        const fieldName = attribute.value;
        initialState[fieldName] = get(row, fieldName, "");
    });
    return initialState;
};

const chartTypes = {
    LINE: ["xAxis", "yAxis"],
    BAR: ["xAxis", "yAxis"],
    COLUMN: ["xAxis", "yAxis"],
    PIE: ["label", "value"],
    DOUGHNUT: ["label", "value"],
    SCATTER: ["xAxis", "yAxis"],
    AREA: ["xAxis", "yAxis"],
    STACKED_BAR: ["xAxis", "yAxis", "groupBy"],
    BUBBLE: ["xAxis", "yAxis", "size"],
    HEATMAP: ["row", "column", "value"],
    TREEMAP: ["category", "value"],
    RADAR: ["category", "value"],
    HISTOGRAM: ["bin", "frequency"],
    WATERFALL: ["category", "value", "change"],
    FUNNEL: ["stage", "value"],
    GANTT: ["task", "startDate", "endDate"],
    GAUGE: ["value", "min", "max"],
    CANDLESTICK: ["date", "open", "high", "low", "close"],
    SANKEY: ["source", "target", "value"],
    BUBBLE_MAP: ["latitude", "longitude", "value"],
};

const DashboardItemDetail = () => {
    const [formData, setFormData] = useState({
        dashboardId: "",
        name: "",
        row: "",
        column: "",
        expandH: "",
        expandV: "",
        requestId: "",
        chartType: "",
        chartMappings: {},
        viewType: "SINGLE_DATA_OBJECT",
        isRealtime: false,
        range: false,
        rangeType: "date", // Default value for range type
        defaultParameters: {},
        startRange: "",
        endRange: "",
        defaultRangeValue: "",
        customViewUrl: "", // New field for the CUSTOM_VIEW URL
    });

    const [requests, setRequests] = useState([]);
    const [requestParameters, setRequestParameters] = useState([]);
    const [responseParameters, setResponseParameters] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const url = new URL(window.location.href);
            const itemId = url.pathname.split("/")[2];
            getItemDetail(DashboardItem.basePath, itemId);
        };

        fetchData();
    }, [DashboardItem.attributes]);

    const getItemDetail = (basePath, itemId) => {
        let getClient = getApiPath(basePath, VIEW, itemId);
        if (itemId !== "add-item") {
            ApiCaller.callGetApi(
                getClient,
                {},
                (response) => {
                    setFormData(response);
                },
                (error) => {
                    console.error("Error fetching item details:", error);
                }
            );
        }
    };

    useEffect(() => {
        const fetchRequests = async () => {
            const jsonRequest = { page: 0, size: 200, searchFilters: [], sortRules: [] };
            ApiCaller.callPostApi(
                getApiPath(GENERIC_REQUEST, GENERIC_PAGINATED_KEY_VALUE_LIST),
                jsonRequest,
                {},
                (response) => {
                    setRequests(response.data.map((d) => ({ value: d.value, label: d.key })));
                },
                (error) => {
                    console.error("Error fetching requests:", error);
                }
            );
        };
        fetchRequests();
    }, []);

    useEffect(() => {
        if (formData.requestId) {
            ApiCaller.callPostApi(
                PARTICULAR_REQUEST_PARAMETER_LIST_GET.replace("{request-id}", formData.requestId),
                null,
                {},
                (response) => {
                    setRequestParameters(response.data);
                },
                (err) => {
                    console.error("Error fetching request parameter list:", err);
                }
            );
            // Reponse param
            ApiCaller.callPostApi(
                PARTICULAR_RESPONSE_PARAMETER_LIST_GET.replace("{request-id}", formData.requestId),
                null,
                {},
                (response) => {
                    setResponseParameters(response.data);
                },
                (err) => {
                    console.error("Error fetching response parameter list:", err);
                }
            );

        } else {
            setRequestParameters([]);
        }
    }, [formData.requestId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => {
            const updatedFormData = { ...prev, [name]: type === "checkbox" ? checked : value };

            if (name === "rangeType") {
                // Update defaultRangeValue based on rangeType
                if (value === "date") {
                    updatedFormData.defaultRangeValue = prev.startRange || "TODAY"; // Default to "TODAY"
                } else if (value === "number") {
                    updatedFormData.defaultRangeValue = `${prev.startRange || "0"}-${prev.endRange || "10"}`;
                }
            }

            if (name === "startRange" || name === "endRange") {
                // Update defaultRangeValue for number type
                if (prev.rangeType === "number") {
                    updatedFormData.defaultRangeValue = `${updatedFormData.startRange || "0"}-${updatedFormData.endRange || "10"}`;
                }
            }

            if (name === "defaultRangeValue" && prev.rangeType === "date") {
                // Directly set defaultRangeValue for date type
                updatedFormData.defaultRangeValue = value;
            }
            if (name === "chartType") {
                updatedFormData.chartMappings = { yAxis: [] }; // Reset chart mappings on chart type change
            }

            return updatedFormData;
        });
    };

    const handleMappingChange = (field, parameter, index = null) => {
        setFormData((prev) => {
            const updatedMappings = { ...prev.chartMappings };

            if (field === "yAxis") {
                if (index !== null) {
                    updatedMappings.yAxis[index] = parameter;
                } else {
                    updatedMappings.yAxis = [...updatedMappings.yAxis, parameter];
                }
            } else {
                updatedMappings[field] = parameter;
            }

            return { ...prev, chartMappings: updatedMappings };
        });
    };

    const removeYAxisMapping = (index) => {
        setFormData((prev) => {
            const updatedMappings = { ...prev.chartMappings };
            updatedMappings.yAxis.splice(index, 1);
            return { ...prev, chartMappings: updatedMappings };
        });
    };


    const handleParameterChange = (paramName, value) => {
        setFormData((prev) => ({
            ...prev,
            defaultParameters: {
                ...prev.defaultParameters,
                [paramName]: value,
            },
        }));
    };

    const filteredParameters = requestParameters.filter(
        (param) => param.name !== formData.startRange && param.name !== formData.endRange
    );

    const handleSubmit = () => {

        const url = getApiPath(DashboardItem.basePath, ADD_UPDATE);
            
                // Call API to save data
                ApiCaller.callPostApi(
                    url,
                    formData,
                    {}, // Headers if needed
                    (response) => {
                        toast.success(
                            `${DashboardItem.label} ${formData?.id ? "Updated" : "Created"} Successfully`
                        );
                    },
                    (error) => {
                        toast.error(
                            error.response?.data?.message || "An error occurred while saving"
                        );
                    }
                );

    };

    return (
        <div className="dashboard-form">
            <input type="hidden" name="dashboardId" value={formData.dashboardId} />

            <div className="form-group">
                <label>Name:</label>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="form-group">
                <label>Row:</label>
                <input
                    type="number"
                    name="row"
                    value={formData.row}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="form-group">
                <label>Column:</label>
                <input
                    type="number"
                    name="column"
                    value={formData.column}
                    onChange={handleChange}
                    required
                />
            </div>

            <div className="form-group">
                <label>Horizontal Expand:</label>
                <input
                    type="number"
                    name="expandH"
                    value={formData.expandH}
                    onChange={handleChange}
                />
            </div>

            <div className="form-group">
                <label>Vertical Expand:</label>
                <input
                    type="number"
                    name="expandV"
                    value={formData.expandV}
                    onChange={handleChange}
                />
            </div>

            <div className="form-group">
                <label>Request ID:</label>
                <select
                    name="requestId"
                    value={formData.requestId}
                    onChange={handleChange}
                    required
                >
                    <option value="">Select a request</option>
                    {requests.map((req) => (
                        <option key={req.value} value={req.value}>
                            {req.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label>View Type:</label>
                <select
                    name="viewType"
                    value={formData.viewType}
                    onChange={handleChange}
                >
                    <option value="SINGLE_DATA_OBJECT">SINGLE_DATA_OBJECT</option>
                    <option value="TABLE_VIEW">TABLE_VIEW</option>
                    <option value="CHART_VIEW">CHART_VIEW</option>
                    <option value="CUSTOM_VIEW">CUSTOM_VIEW</option>
                </select>
            </div>
            
            {formData.viewType === "CHART_VIEW" && (
                <>
                    <div className="form-group">
                        <label>Chart Type:</label>
                        <select
                            name="chartType"
                            value={formData.chartType}
                            onChange={handleChange}
                        >
                            <option value="">Select Chart Type</option>
                            {Object.keys(chartTypes).map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>

                    {formData.chartType && (
                        <div className="form-section">
                            <h4>Map Chart Fields</h4>
                            {chartTypes[formData.chartType]
                                .filter((field) => field !== "yAxis")
                                .map((field) => (
                                    <div className="form-group" key={field}>
                                        <label>{field}:</label>
                                        <select
                                            value={formData.chartMappings[field] || ""}
                                            onChange={(e) =>
                                                handleMappingChange(field, e.target.value)
                                            }
                                        >
                                            <option value="">Select Parameter</option>
                                            {responseParameters.map((param) => (
                                                <option key={param.name} value={param.name}>
                                                    {param.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}

                            <h4>Y-Axis Mappings</h4>
                            {formData.chartMappings.yAxis.map((yAxisParam, index) => (
                                <div className="form-group" key={`yAxis-${index}`}>
                                    <label>Y-Axis {index + 1}:</label>
                                    <select
                                        value={yAxisParam || ""}
                                        onChange={(e) =>
                                            handleMappingChange("yAxis", e.target.value, index)
                                        }
                                    >
                                        <option value="">Select Parameter</option>
                                        {responseParameters.map((param) => (
                                            <option key={param.name} value={param.name}>
                                                {param.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => removeYAxisMapping(index)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => handleMappingChange("yAxis", "")}
                            >
                                Add Y-Axis Mapping
                            </button>
                        </div>
                    )}
                </>
            )}

            {formData.viewType === "CUSTOM_VIEW" && (
                <div className="form-group">
                    <label>Custom View URL:</label>
                    <input
                        type="text"
                        name="customViewUrl"
                        value={formData.customViewUrl}
                        onChange={handleChange}
                        placeholder="Enter custom view URL"
                    />
                </div>
            )}

            <div className="form-group">
                <label>
                    <input
                        type="checkbox"
                        name="isRealtime"
                        checked={formData.isRealtime}
                        onChange={handleChange}
                    />
                    Is Realtime
                </label>
            </div>

            {requestParameters.length > 1 && (
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            name="range"
                            checked={formData.range}
                            onChange={handleChange}
                        />
                        Is Range
                    </label>
                </div>
            )}

            {filteredParameters.length > 0 && (
                <div className="form-section">
                    <h4>Default Parameter Values</h4>
                    {filteredParameters.map((param) => (
                        <div className="form-group" key={param.name}>
                            <label>{param.name}:</label>
                            <input
                                type="text"
                                value={(formData.defaultParameters && formData.defaultParameters[param.name]) || ""}
                                onChange={(e) => handleParameterChange(param.name, e.target.value)}
                            />
                        </div>
                    ))}
                </div>
            )}

            {formData.range && requestParameters.length > 1 && (
                <div className="form-section">
                    <h4>Range Selection</h4>
                    <div className="form-group">
                        <label>Start of Range:</label>
                        <select
                            name="startRange"
                            value={formData.startRange}
                            onChange={handleChange}
                        >
                            <option value="">Select Parameter</option>
                            {requestParameters.map((param) => (
                                <option key={param.name} value={param.name}>
                                    {param.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>End of Range:</label>
                        <select
                            name="endRange"
                            value={formData.endRange}
                            onChange={handleChange}
                        >
                            <option value="">Select Parameter</option>
                            {requestParameters.map((param) => (
                                <option key={param.name} value={param.name}>
                                    {param.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Range Type:</label>
                        <select
                            name="rangeType"
                            value={formData.rangeType}
                            onChange={handleChange}
                        >
                            <option value="date">Date</option>
                            <option value="number">Number</option>
                        </select>
                    </div>

                    {formData.rangeType === "date" && (
                        <div className="form-group">
                            <label>Date Range:</label>
                            <select
                                name="defaultRangeValue"
                                value={formData.defaultRangeValue}
                                onChange={handleChange}
                            >
                                <option value="TODAY">Today</option>
                                <option value="YESTERDAY">Yesterday</option>
                                <option value="THIS_WEEK">This Week</option>
                                <option value="LAST_WEEK">Last Week</option>
                                <option value="THIS_MONTH">This Month</option>
                                <option value="LAST_MONTH">Last Month</option>
                                <option value="THIS_YEAR">This Year</option>
                                <option value="LAST_YEAR">Last Year</option>
                            </select>
                        </div>
                    )}

                    {formData.rangeType === "number" && (
                        <div className="form-group">
                            <label>Start Range:</label>
                            <input
                                type="number"
                                name="startRange"
                                value={formData.startRange}
                                onChange={handleChange}
                            />
                            <label>End Range:</label>
                            <input
                                type="number"
                                name="endRange"
                                value={formData.endRange}
                                onChange={handleChange}
                            />
                        </div>
                    )}
                </div>
            )}

            <button
                type="submit"
                className="submit-button"
                onClick={handleSubmit}
            >
                Submit
            </button>
        </div>
    );
};

export default DashboardItemDetail;
